/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { sortBy } from 'lodash'

import type { SaasAuthMfaDeviceResponse } from '@modules/cloud-api/v1/types'
import type { DeviceType } from '@modules/mfa-management/types'

export function filterMfaDevices({
  devices,
  onlyActive,
  onlyStrong,
}: {
  devices: SaasAuthMfaDeviceResponse[] | undefined
  onlyActive?: true
  onlyStrong?: true
}): SaasAuthMfaDeviceResponse[] {
  return (devices || [])
    .filter((device) => !onlyActive || device.status === 'ACTIVE')
    .filter((device) => !onlyStrong || device.device_type !== 'SMS')
}

// If there are only weak devices, they can be used to authenticate,
// otherwise, only return strong devices and exclude weak devices.
export function filterWeakMfaDevices({
  devices,
}: {
  devices: SaasAuthMfaDeviceResponse[] | undefined
}): SaasAuthMfaDeviceResponse[] {
  if (hasOnlyWeakMfaDevice(devices)) {
    return filterMfaDevices({ devices, onlyActive: true })
  }

  return filterMfaDevices({ devices, onlyActive: true, onlyStrong: true })
}

export function hasStrongMfaDevice(devices: SaasAuthMfaDeviceResponse[] | undefined): boolean {
  return filterMfaDevices({ devices, onlyActive: true, onlyStrong: true }).length > 0
}

export function hasOnlyWeakMfaDevice(devices: SaasAuthMfaDeviceResponse[] | undefined): boolean {
  return (
    filterMfaDevices({ devices, onlyActive: true }).length > 0 &&
    filterMfaDevices({ devices, onlyActive: true, onlyStrong: true }).length === 0
  )
}

export function getStrongDeviceTypes(): DeviceType[] {
  const deviceTypes: DeviceType[] = ['WEBAUTHN', 'GOOGLE', 'EMAIL']

  return sortBy(deviceTypes, deviceTypesDisplayOrder)
}

export function getAllDeviceTypes(): DeviceType[] {
  const deviceTypes: DeviceType[] = ['WEBAUTHN', 'GOOGLE', 'EMAIL', 'SMS']

  return sortBy(deviceTypes, deviceTypesDisplayOrder)
}
export function deviceTypesDisplayOrder(deviceType: DeviceType): number {
  return ['WEBAUTHN', 'GOOGLE', 'EMAIL', 'SMS'].indexOf(deviceType)
}

export function deviceTypeIcon(deviceType: DeviceType): string {
  switch (deviceType) {
    case 'WEBAUTHN':
      return 'key'
    case 'GOOGLE':
      return 'desktop'
    case 'EMAIL':
      return 'email'
    case 'SMS':
      return 'mobile'
    default:
      return ''
  }
}
